import React, { useMemo, useState } from 'react'
import { Col, Modal, Row } from 'react-bootstrap'
import Swal from 'sweetalert2'

import useTranslation from '@/Hooks/UseTranslation'
import { WeeklyCommitmentCandidate } from '@/Helpers/WeeklyCommitmentHelper'
import {
  WeeklyCommitment,
  WeeklyWorkPlan,
  WeeklyWorkPlanDraftCommitment,
} from '@/Services/WeeklyWorkPlanService'
import { CommitmentFailure } from '@/Services/CncLearningService'
import ContentBlock from '@/Components/ProgrammationManager/components/ContentBlock'
import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import Times from '@/Components/SvgImages/Times'

import '../ProgrammationManager/ProgrammationManager.scss'
import './WeeklyCommitmentsModal.scss'

interface DraftRow {
  selected: boolean
  promisedDate: string
  conditions: string
  performerIds: string[]
}

interface Props {
  show: boolean
  candidates: WeeklyCommitmentCandidate[]
  commitments: WeeklyCommitment[]
  plan: WeeklyWorkPlan | null
  periodStart: string
  periodEnd: string
  isPastPeriod?: boolean
  saving: boolean
  publishing: boolean
  closing: boolean
  processingCommitmentId: string
  canManagePlan: boolean
  currentUserId: string
  failures: CommitmentFailure[]
  error?: string
  onHide: () => void
  onSave: (commitments: WeeklyWorkPlanDraftCommitment[], retrospectiveReason?: string) => Promise<void>
  onPublish: (retrospectiveReason?: string) => Promise<void>
  onDeclareComplete: (commitmentId: string, completedAt?: string) => Promise<void>
  onResolve: (commitmentId: string, accepted: boolean, comment?: string) => Promise<void>
  onClosePlan: () => Promise<void>
  onOpenFailure: (failure: CommitmentFailure, taskName: string) => void
}

const taskId = (commitment: WeeklyCommitment) => `${commitment.taskSnapshot?.id || commitment.task || ''}`

const WeeklyCommitmentsModal = ({
  show,
  candidates,
  commitments,
  plan,
  periodStart,
  periodEnd,
  isPastPeriod = false,
  saving,
  publishing,
  closing,
  processingCommitmentId,
  canManagePlan,
  currentUserId,
  failures,
  error,
  onHide,
  onSave,
  onPublish,
  onDeclareComplete,
  onResolve,
  onClosePlan,
  onOpenFailure,
}: Props) => {
  const { t } = useTranslation()
  const retrospective = isPastPeriod || Boolean(plan?.retrospective)
  const editable = (!plan || plan.status === 'draft') && (!retrospective || canManagePlan)
  const commitmentByTask = useMemo(() => new Map(
    commitments.map((commitment) => [ taskId(commitment), commitment ]),
  ), [ commitments ])

  const rows = useMemo(() => {
    const result = [ ...candidates ]
    const candidateIds = new Set(candidates.map((candidate) => candidate.taskId))

    commitments.forEach((commitment) => {
      const id = taskId(commitment)
      if (!id || candidateIds.has(id)) return
      result.push({
        taskId: id,
        name: commitment.taskSnapshot?.name || t('WEEKLY_PLAN:UnknownTask'),
        type: commitment.taskSnapshot?.type || '',
        performerIds: (commitment.performerIds || []).map(String),
        performerLabels: [],
        suggestedPromisedDate: commitment.promisedDate?.slice(0, 10) || periodEnd,
        openRestrictions: commitment.restrictionSnapshot?.state === 'blocked' ? 1 : 0,
      })
    })

    return result
  }, [ candidates, commitments, periodEnd, t ])

  const [ draft, setDraft ] = useState<Record<string, DraftRow>>(() => rows.reduce((state, candidate) => {
    const commitment = commitmentByTask.get(candidate.taskId)
    state[candidate.taskId] = {
      selected: Boolean(commitment),
      promisedDate: commitment?.promisedDate?.slice(0, 10) || candidate.suggestedPromisedDate,
      conditions: (commitment?.conditionsOfSatisfaction || []).join('\n'),
      performerIds: (commitment?.performerIds || candidate.performerIds || []).map(String),
    }
    return state
  }, {} as Record<string, DraftRow>))
  const [ dirty, setDirty ] = useState(false)
  const [ retrospectiveReason, setRetrospectiveReason ] = useState(plan?.retrospectiveReason || '')

  const selectedCount = Object.values(draft).filter((row) => row.selected).length
  const pendingResults = commitments.filter((commitment) => commitment.eligible && !commitment.result).length
  const busy = saving || publishing || closing || Boolean(processingCommitmentId)

  const updateRow = (id: string, changes: Partial<DraftRow>) => {
    setDirty(true)
    setDraft((current) => ({
      ...current,
      [id]: { ...current[id], ...changes },
    }))
  }

  const selectAll = (selected: boolean) => {
    setDirty(true)
    setDraft((current) => Object.keys(current).reduce((next, id) => {
      next[id] = { ...current[id], selected }
      return next
    }, {} as Record<string, DraftRow>))
  }

  const save = async () => {
    const payload = rows
      .filter((candidate) => draft[candidate.taskId]?.selected)
      .map((candidate) => ({
        taskId: candidate.taskId,
        performerIds: draft[candidate.taskId].performerIds,
        promisedDate: draft[candidate.taskId].promisedDate || undefined,
        conditionsOfSatisfaction: draft[candidate.taskId].conditions
          .split('\n')
          .map((condition) => condition.trim())
          .filter(Boolean),
      }))

    try {
      if (retrospective) {
        await onSave(payload, retrospectiveReason.trim())
      } else {
        await onSave(payload)
      }
      setDirty(false)
    } catch (error) {
      // The parent renders the canonical API error and the draft remains dirty.
    }
  }

  const publish = async () => {
    if (!plan || plan.status !== 'draft' || dirty) return

    const result = await Swal.fire({
      title: t('WEEKLY_PLAN:PublishConfirmTitle'),
      text: t('WEEKLY_PLAN:PublishConfirmText'),
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: t('WEEKLY_PLAN:Cancel'),
      confirmButtonText: t('WEEKLY_PLAN:PublishConfirmButton'),
      width: '500px',
    })

    if (!result.isConfirmed) return

    try {
      if (retrospective) {
        await onPublish(retrospectiveReason.trim())
      } else {
        await onPublish()
      }
    } catch (error) {
      // The parent renders the canonical API error and keeps the modal open.
    }
  }

  const declareComplete = async (commitment: WeeklyCommitment) => {
    const today = new Date().toISOString().slice(0, 10)
    const result = await Swal.fire({
      title: t('WEEKLY_PLAN:DeclareCompleteTitle'),
      text: commitment.taskSnapshot?.name || t('WEEKLY_PLAN:UnknownTask'),
      input: 'text',
      inputValue: today,
      inputPlaceholder: 'AAAA-MM-DD',
      inputValidator: (value) => {
        const completedAt = new Date(`${value}T00:00:00`)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(completedAt.getTime()) || value > today) {
          return t('WEEKLY_PLAN:InvalidCompletionDate')
        }
        return null
      },
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: t('WEEKLY_PLAN:Cancel'),
      confirmButtonText: t('WEEKLY_PLAN:DeclareCompleteButton'),
      width: '500px',
    })

    if (!result.isConfirmed) return
    try {
      await onDeclareComplete(commitment._id, result.value || undefined)
    } catch (error) {
      // The parent renders the canonical API error and keeps the modal open.
    }
  }

  const acceptCompletion = async (commitment: WeeklyCommitment) => {
    const result = await Swal.fire({
      title: t('WEEKLY_PLAN:AcceptCompletionTitle'),
      text: commitment.taskSnapshot?.name || t('WEEKLY_PLAN:UnknownTask'),
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: t('WEEKLY_PLAN:Cancel'),
      confirmButtonText: t('WEEKLY_PLAN:AcceptCompletionButton'),
      width: '500px',
    })

    if (!result.isConfirmed) return
    try {
      await onResolve(commitment._id, true)
    } catch (error) {
      // The parent renders the canonical API error and keeps the modal open.
    }
  }

  const rejectCompletion = async (commitment: WeeklyCommitment) => {
    const result = await Swal.fire({
      title: t('WEEKLY_PLAN:RejectCompletionTitle'),
      text: commitment.taskSnapshot?.name || t('WEEKLY_PLAN:UnknownTask'),
      input: 'textarea',
      inputPlaceholder: t('WEEKLY_PLAN:RejectReasonPlaceholder'),
      inputValidator: (value) => value?.trim() ? null : t('WEEKLY_PLAN:RejectReasonRequired'),
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: t('WEEKLY_PLAN:Cancel'),
      confirmButtonText: t('WEEKLY_PLAN:RejectCompletionButton'),
      width: '500px',
    })

    if (!result.isConfirmed) return
    try {
      await onResolve(commitment._id, false, result.value?.trim())
    } catch (error) {
      // The parent renders the canonical API error and keeps the modal open.
    }
  }

  const closePlan = async () => {
    const result = await Swal.fire({
      title: t('WEEKLY_PLAN:ClosePlanConfirmTitle'),
      text: t('WEEKLY_PLAN:ClosePlanConfirmText'),
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: t('WEEKLY_PLAN:Cancel'),
      confirmButtonText: t('WEEKLY_PLAN:ClosePlanConfirmButton'),
      width: '500px',
    })

    if (!result.isConfirmed) return
    try {
      await onClosePlan()
    } catch (error) {
      // The parent renders the canonical API error and keeps the modal open.
    }
  }

  const renderCommitmentResult = (commitment: WeeklyCommitment) => {
    if (commitment.status === 'resolved') {
      const failure = failures.find((row) => `${row.weeklyCommitment}` === commitment._id)
      return (
        <div className="weekly-commitments-modal__row-actions">
          <strong>{t(`WEEKLY_PLAN:Result_${commitment.result || 'not_kept'}`)}</strong>
          {commitment.result === 'not_kept' && failure && canManagePlan && (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => onOpenFailure(failure, commitment.taskSnapshot?.name || t('WEEKLY_PLAN:UnknownTask'))}>
              {failure.status === 'pending_analysis' ? t('WEEKLY_PLAN:AnalyzeCnc') : t('WEEKLY_PLAN:ViewCnc')}
            </button>
          )}
          {commitment.result === 'not_kept' && failure?.causeSnapshot?.name && (
            <span>{failure.causeSnapshot.code} · {failure.causeSnapshot.name}</span>
          )}
        </div>
      )
    }

    const isPerformer = (commitment.performerIds || []).map(String).includes(currentUserId)
    const canDeclare = canManagePlan || isPerformer
    const processing = processingCommitmentId === commitment._id
    const showDeclare = commitment.status === 'committed' && canDeclare
    const showAccept = commitment.status === 'declared_complete' && canManagePlan
    const showReject = canManagePlan && [ 'committed', 'declared_complete' ].includes(commitment.status)

    if (!showDeclare && !showAccept && !showReject) {
      return <span>{t(`WEEKLY_PLAN:CommitmentStatus_${commitment.status}`)}</span>
    }

    return (
      <div className="weekly-commitments-modal__row-actions">
        {showDeclare && (
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => declareComplete(commitment)}>
            {processing ? t('WEEKLY_PLAN:Processing') : t('WEEKLY_PLAN:DeclareCompleteButton')}
          </button>
        )}
        {showAccept && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => acceptCompletion(commitment)}>
            {processing ? t('WEEKLY_PLAN:Processing') : t('WEEKLY_PLAN:AcceptCompletionButton')}
          </button>
        )}
        {showReject && (
          <button type="button" className="btn btn-danger" disabled={busy} onClick={() => rejectCompletion(commitment)}>
            {t('WEEKLY_PLAN:RejectCompletionButton')}
          </button>
        )}
      </div>
    )
  }

  const handleHide = () => {
    if (!busy) onHide()
  }

  return (
    <Modal
      show={show}
      dialogClassName="programmation-manager-modal weekly-commitments-modal"
      onHide={handleHide}
      keyboard={!busy}
      {...(busy ? { backdrop: 'static' } : {})}
    >
      <Modal.Header>
        <div className="weekly-commitments-modal__navigator">
          <i className="fas fa-clipboard-check" aria-hidden="true" />
          <div>
            <strong>{t('WEEKLY_PLAN:CommitmentsTitle')}</strong>
            <small>{periodStart} — {periodEnd}</small>
          </div>
        </div>
        <button
          type="button"
          className="modal-options-button"
          aria-label={t('WEEKLY_PLAN:Close')}
          onClick={handleHide}
          disabled={busy}
        >
          {busy
            ? <Spinner height={24} width={24} />
            : <Times height={24} width={24} />}
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className="programmation-manager-body-scrollable scroller">
          <Row>
            <Col lg={12}>
              <div className="programmation-manager-desktop-scrollable scroller">
                <ContentBlock title={t('WEEKLY_PLAN:ActivitiesSection')}>
                  <div className="weekly-commitments-modal__intro">
                    <p>{editable
                      ? t('WEEKLY_PLAN:CommitmentsDraftDescription')
                      : t('WEEKLY_PLAN:CommitmentsSnapshotDescription')}
                    </p>
                    <div className="weekly-commitments-modal__selection">
                      <strong>{selectedCount} {t('WEEKLY_PLAN:Selected')}</strong>
                      {editable && (
                        <>
                          <button type="button" className="btn btn-link" onClick={() => selectAll(true)}>
                            {t('WEEKLY_PLAN:SelectAll')}
                          </button>
                          <button type="button" className="btn btn-link" onClick={() => selectAll(false)}>
                            {t('WEEKLY_PLAN:ClearSelection')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {retrospective && (
                    <div className="weekly-commitments-modal__retrospective" role="note">
                      <div>
                        <strong>{t('WEEKLY_PLAN:RetrospectiveBadge')}</strong>
                        <p>{t('WEEKLY_PLAN:RetrospectivePolicy')}</p>
                      </div>
                      {editable ? (
                        <label>
                          <span>{t('WEEKLY_PLAN:RetrospectiveReason')}</span>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={retrospectiveReason}
                            placeholder={t('WEEKLY_PLAN:RetrospectiveReasonPlaceholder')}
                            onChange={(event) => {
                              setRetrospectiveReason(event.target.value)
                              setDirty(true)
                            }}
                          />
                          {!retrospectiveReason.trim() && <small>{t('WEEKLY_PLAN:RetrospectiveReasonRequired')}</small>}
                        </label>
                      ) : plan?.retrospectiveReason ? (
                        <div className="weekly-commitments-modal__retrospective-reason">
                          <span>{t('WEEKLY_PLAN:RetrospectiveReason')}</span>
                          <p>{plan.retrospectiveReason}</p>
                        </div>
                      ) : !canManagePlan ? (
                        <small>{t('WEEKLY_PLAN:RetrospectiveNotAuthorized')}</small>
                      ) : null}
                    </div>
                  )}

                  {error && <div className="weekly-commitments-modal__error" role="alert">{error}</div>}

                  {!rows.length && (
                    <div className="weekly-commitments-modal__empty">
                      <i className="fas fa-calendar-check" aria-hidden="true" />
                      <h3>{t('WEEKLY_PLAN:NoCandidateTasks')}</h3>
                      <p>{t('WEEKLY_PLAN:NoCandidateTasksDescription')}</p>
                    </div>
                  )}

                  {rows.length > 0 && (
                    <div className="weekly-commitments-modal__table-wrap">
                      <table className="table weekly-commitments-modal__table">
                        <thead>
                          <tr>
                            {editable && <th className="weekly-commitments-modal__select-column"><span className="sr-only">{t('WEEKLY_PLAN:Select')}</span></th>}
                            <th>{t('WEEKLY_PLAN:Activity')}</th>
                            <th>{t('WEEKLY_PLAN:Responsible')}</th>
                            <th>{t('WEEKLY_PLAN:Readiness')}</th>
                            <th>{t('WEEKLY_PLAN:PromisedDate')}</th>
                            <th>{t('WEEKLY_PLAN:Conditions')}</th>
                            {!editable && <th>{t('WEEKLY_PLAN:Result')}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((candidate) => {
                            const saved = commitmentByTask.get(candidate.taskId)
                            const row = draft[candidate.taskId]
                            const selected = editable ? row.selected : Boolean(saved)
                            const ready = saved
                              ? saved.restrictionSnapshot?.state !== 'blocked'
                              : candidate.openRestrictions === 0

                            if (!editable && !saved) return null

                            return (
                              <tr key={candidate.taskId} className={!selected ? 'is-unselected' : ''}>
                                {editable && (
                                  <td className="weekly-commitments-modal__select-column">
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      aria-label={`${t('WEEKLY_PLAN:Select')} ${candidate.name}`}
                                      onChange={(event) => updateRow(candidate.taskId, { selected: event.target.checked })}
                                    />
                                  </td>
                                )}
                                <td>
                                  <strong>{candidate.position ? `${candidate.position} · ` : ''}{candidate.name}</strong>
                                  <small>{candidate.type}</small>
                                </td>
                                <td>
                                  {(candidate.performerLabels.length ? candidate.performerLabels : row.performerIds).join(', ') || (
                                    <span className="weekly-commitments-modal__warning">{t('WEEKLY_PLAN:NoResponsible')}</span>
                                  )}
                                </td>
                                <td>
                                  <span className={`weekly-commitments-modal__readiness ${ready ? 'is-ready' : 'is-blocked'}`}>
                                    {ready ? t('WEEKLY_PLAN:Ready') : `${t('WEEKLY_PLAN:Blocked')} · ${candidate.openRestrictions}`}
                                  </span>
                                </td>
                                <td>
                                  {editable ? (
                                    <input
                                      type="date"
                                      className="form-control"
                                      min={periodStart}
                                      max={periodEnd}
                                      value={row.promisedDate}
                                      disabled={!selected}
                                      onChange={(event) => updateRow(candidate.taskId, { promisedDate: event.target.value })}
                                    />
                                  ) : (saved?.promisedDate ? saved.promisedDate.slice(0, 10) : '—')}
                                </td>
                                <td>
                                  {editable ? (
                                    <textarea
                                      className="form-control"
                                      rows={2}
                                      value={row.conditions}
                                      disabled={!selected}
                                      placeholder={t('WEEKLY_PLAN:ConditionsPlaceholder')}
                                      onChange={(event) => updateRow(candidate.taskId, { conditions: event.target.value })}
                                    />
                                  ) : ((saved?.conditionsOfSatisfaction || []).join(' · ') || '—')}
                                </td>
                                {!editable && <td>{saved ? renderCommitmentResult(saved) : '—'}</td>}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {editable && rows.length > 0 && (
                    <div className="weekly-commitments-modal__actions">
                      <span>{dirty && plan
                        ? t('WEEKLY_PLAN:SaveBeforePublish')
                        : t('WEEKLY_PLAN:DraftDoesNotPublish')}
                      </span>
                      <div>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={busy || selectedCount === 0 || !dirty || (retrospective && !retrospectiveReason.trim())}
                          onClick={save}
                        >
                          <span className="icon-guardar" aria-hidden="true" />{' '}
                          {saving ? t('WEEKLY_PLAN:SavingDraft') : t('WEEKLY_PLAN:SaveDraft')}
                        </button>
                        {plan?.status === 'draft' && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={busy || dirty || selectedCount === 0 || (retrospective && !retrospectiveReason.trim())}
                            onClick={publish}
                          >
                            <i className="fas fa-lock" aria-hidden="true" />{' '}
                            {publishing ? t('WEEKLY_PLAN:PublishingPlan') : t('WEEKLY_PLAN:PublishPlan')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {plan?.status === 'published' && rows.length > 0 && (
                    <div className="weekly-commitments-modal__actions">
                      <span>{pendingResults > 0
                        ? t('WEEKLY_PLAN:PendingResults').replace('{count}', `${pendingResults}`)
                        : t('WEEKLY_PLAN:ReadyToClose')}
                      </span>
                      {canManagePlan && (
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={busy || pendingResults > 0}
                            onClick={closePlan}
                          >
                            <i className="fas fa-lock" aria-hidden="true" />{' '}
                            {closing ? t('WEEKLY_PLAN:ClosingPlan') : t('WEEKLY_PLAN:ClosePlan')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </ContentBlock>
              </div>
            </Col>
          </Row>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default WeeklyCommitmentsModal
